import { useState, useRef, useEffect } from 'react';
import { UploadCloud, FileText, AlertCircle, Loader2, ChevronRight, X } from 'lucide-react';
import useStudentIDVerification from '../../../../hooks/useStudentIDVerification';
import WebcamCapture from './WebcamCapture';
import { toast } from 'sonner';

const IdUploadStep = ({ onNext, onCancel, cancelText = 'Cancel', allowWebcam = true, submitText = 'Verify & Proceed' }) => {
  const {
    status,
    error: idUploadError,
    isUploading,
    isPolling,
    uploadIDCard
  } = useStudentIDVerification();

  const [uploadMode, setUploadMode] = useState('select'); // 'select' or 'webcam'
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const fileInputRef = useRef(null);

  // Auto-advance to the webcam face verification step when student ID is successfully verified
  useEffect(() => {
    if (status === 'verified') {
      onNext();
    }
  }, [status, onNext]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file (PNG, JPG, JPEG).');
        return;
      }
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWebcamCapture = ({ file, blob }) => {
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(blob));
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error('Please select or capture a student ID image first.');
      return;
    }
    
    try {
      await uploadIDCard(selectedFile);
    } catch (err) {
      // Hook handles setting errors and toast notifications
    }
  };

  if (isPolling) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center space-y-6 animate-fade-in">
        <div className="relative flex items-center justify-center">
          {/* Pulsing decoration */}
          <div className="absolute w-24 h-24 rounded-full border-4 border-primary/30 animate-ping" />
          <div className="relative w-20 h-20 rounded-full bg-base-200 border-2 border-primary flex items-center justify-center shadow-lg">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-bold tracking-tight">Validating ID Document...</h3>
          <p className="text-sm text-gray-500 max-w-sm">
            We are analyzing your uploaded ID card in the background. This process uses automated face detection and should only take a few seconds.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          Upload Your Student ID Card
        </h2>
        <p className="text-sm text-gray-500">
          Please upload a clear picture of your physical student identification card. The card must clearly show your face.
        </p>
      </div>

      {/* Tabs */}
      {allowWebcam && (
        <div className="tabs tabs-boxed justify-center gap-2 p-1.5 bg-base-200">
          <button 
            type="button"
            className={`tab ${uploadMode === 'select' ? 'tab-active bg-primary text-primary-content' : ''}`}
            onClick={() => {
              setUploadMode('select');
              setSelectedFile(null);
              setPreviewUrl(null);
            }}
          >
            Upload File
          </button>
          <button 
            type="button"
            className={`tab ${uploadMode === 'webcam' ? 'tab-active bg-primary text-primary-content' : ''}`}
            onClick={() => {
              setUploadMode('webcam');
              setSelectedFile(null);
              setPreviewUrl(null);
            }}
          >
            Use Webcam
          </button>
        </div>
      )}

      {/* Error alerts */}
      {idUploadError && (
        <div className="alert alert-error flex items-start gap-2.5 rounded-xl text-sm border-l-4 border-error">
          <AlertCircle className="w-5 h-5 text-error mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-bold">Validation Error</h4>
            <p>{idUploadError}</p>
          </div>
        </div>
      )}

      {/* File Selector View */}
      {uploadMode === 'select' && (
        <div className="space-y-4">
          {!previewUrl ? (
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-base-300 hover:border-primary transition-colors cursor-pointer rounded-2xl p-8 flex flex-col items-center justify-center text-center bg-base-200/50 hover:bg-base-200 min-h-[220px]"
            >
              <UploadCloud className="w-12 h-12 text-gray-400 mb-3" />
              <span className="font-semibold text-gray-700">Click to upload file</span>
              <span className="text-xs text-gray-400 mt-1">Supports JPEG, JPG, PNG (Max 5MB)</span>
              <input 
                type="file" 
                ref={fileInputRef}
                className="hidden" 
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>
          ) : (
            <div className="relative rounded-2xl overflow-hidden border border-base-300 bg-base-200 max-h-[280px] flex items-center justify-center">
              <img 
                src={previewUrl} 
                alt="Student ID Preview" 
                className="object-contain max-h-[280px] w-full"
              />
              <button 
                type="button"
                onClick={() => {
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
                className="absolute top-3 right-3 btn btn-circle btn-sm bg-base-100 hover:bg-red-500 hover:text-white border-0 shadow-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Webcam View */}
      {uploadMode === 'webcam' && (
        <div className="space-y-4">
          {!selectedFile ? (
            <WebcamCapture 
              overlayType="rectangle"
              guideText="Align your student ID card inside the frame"
              onCapture={handleWebcamCapture}
              onCancel={onCancel}
            />
          ) : (
            <div className="relative rounded-2xl overflow-hidden border border-base-300 bg-base-200 max-h-[280px] flex items-center justify-center">
              <img 
                src={previewUrl} 
                alt="Student ID Preview" 
                className="object-contain max-h-[280px] w-full"
              />
              <button 
                type="button"
                onClick={() => {
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
                className="absolute bottom-4 left-1/2 transform -translate-x-1/2 btn btn-sm btn-outline gap-2 bg-base-100/90 shadow-md hover:bg-base-100"
              >
                Reset & Capture Again
              </button>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-end gap-3 pt-2">
        {onCancel && (
          <button 
            type="button"
            onClick={onCancel}
            className="btn btn-ghost"
            disabled={isUploading}
          >
            {cancelText}
          </button>
        )}
        <button 
          type="button"
          onClick={handleSubmit}
          disabled={!selectedFile || isUploading}
          className="btn btn-primary px-6"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              {submitText}
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default IdUploadStep;
